/**
 * Componente de diálogo de confirmación
 * 
 * Muestra un modal para confirmar acciones importantes.
 * 
 * @module components/common/ConfirmDialog
 * @requires react
 */

import React from 'react';
import Button from './Button';

/**
 * Componente modal para confirmar acciones
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.isOpen - Si es true, el diálogo se muestra
 * @param {string} props.title - Título del diálogo
 * @param {ReactNode} props.message - Mensaje a mostrar
 * @param {string} props.confirmText - Texto del botón de confirmación
 * @param {string} props.cancelText - Texto del botón de cancelación
 * @param {Function} props.onConfirm - Función a ejecutar al confirmar
 * @param {Function} props.onCancel - Función a ejecutar al cancelar
 * @param {string} props.confirmColor - Color del botón de confirmación
 * @param {boolean} props.loading - Si es true, muestra indicador de carga
 * @returns {JSX.Element} Componente ConfirmDialog
 */
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
  confirmColor = 'primary',
  loading = false
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
        onClick={onCancel}
      >
        <div
          className="z-50 w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-e0e3-surface text-e0e3-foreground shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-white/10 px-6 py-4">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
          </div>

          <div className="px-6 py-4 text-sm text-e0e3-muted [&_.text-gray-600]:text-e0e3-muted [&_.text-gray-700]:text-e0e3-muted [&_.text-red-600]:text-red-300 [&_.text-green-600]:text-emerald-300">
            {typeof message === 'string' ? <p className="text-e0e3-muted">{message}</p> : message}
          </div>

          <div className="flex justify-end space-x-2 border-t border-white/10 bg-black/20 px-6 py-4">
            <Button
              color="secondary"
              onClick={onCancel}
              disabled={loading}
            >
              {cancelText}
            </Button>

            <Button
              color={confirmColor}
              onClick={onConfirm}
              loading={loading}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmDialog;
